import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { CloudOff, CloudSync } from 'lucide-react';

import { cn } from '@/lib/utils';

interface AutosaveIconToggleProps {
  readonly enabled: boolean;
  readonly onToggle: (next: boolean) => void;
  readonly disabled?: boolean;
  readonly disabledTitle?: string;
  readonly className?: string;
}

/**
 * PTV2-1327 — the shared autosave on/off control: a SINGLE in-place icon toggle, styled to match the
 * card Collapsed/Expanded control (`CardViewStateControl`): a bordered `h-6` pill that fills
 * `bg-primary` when active. Shows `cloud-sync` when on and `cloud-off` when off; the label is in the
 * tooltip ("Autosave on" / "Autosave off"). Used by both the patient record and the pedigree toolbar.
 */
export function AutosaveIconToggle({
  enabled,
  onToggle,
  disabled = false,
  disabledTitle,
  className,
}: AutosaveIconToggleProps): ReactElement {
  const { t } = useTranslation();
  const on = !disabled && enabled;
  const title =
    disabled && disabledTitle !== undefined
      ? disabledTitle
      : enabled
        ? t('patient.autosave.on', { defaultValue: 'Autosave on' })
        : t('patient.autosave.off', { defaultValue: 'Autosave off' });
  const Icon = enabled ? CloudSync : CloudOff;

  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      title={title}
      aria-label={title}
      disabled={disabled}
      onClick={
        disabled
          ? undefined
          : () => {
              onToggle(!enabled);
            }
      }
      className={cn(
        'inline-flex h-6 w-7 shrink-0 items-center justify-center rounded-md border border-foreground/25 transition-all duration-150',
        disabled && 'cursor-default opacity-40',
        on
          ? 'bg-toggle-selected text-toggle-selected-foreground'
          : 'text-muted-foreground hover:bg-accent hover:text-foreground',
        className,
      )}
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}
